import React, { useState, useMemo, useEffect } from 'react';
import { Trophy, ArrowUpDown, ChevronUp, ChevronDown, Check, X } from 'lucide-react';
import type { BrakingLapEntry } from '@/types/telemetry';
import { formatTime } from '@/lib/format';
import { useI18n } from '@/context/I18nContext';
import { Pagination } from '@/components/common/Pagination';

interface BrakingLapTableProps {
  laps: BrakingLapEntry[];
  activeLap: BrakingLapEntry | null;
  comparisonLap?: BrakingLapEntry | null;
  onSelectActiveLap: (lap: BrakingLapEntry) => void;
  onSelectComparisonLap: (lap: BrakingLapEntry | null) => void;
}

type SortField =
  | 'lapNumber'
  | 'driver'
  | 'lapTime'
  | 'initialSpeed'
  | 'minSpeed'
  | 'speedDrop'
  | 'distance'
  | 'duration'
  | 'peakDecel'
  | 'trailScore';

type SortOrder = 'asc' | 'desc';

const PAGE_SIZE = 10;

export function BrakingLapTable({
  laps,
  activeLap,
  comparisonLap,
  onSelectActiveLap,
  onSelectComparisonLap,
}: BrakingLapTableProps) {
  const { t } = useI18n();
  const [sortField, setSortField] = useState<SortField>('lapTime');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(1);

  // Reset page if lap list changes
  useEffect(() => {
    setPage(1);
  }, [laps.length]);

  const bestLapTime = useMemo(() => {
    const valid = laps.map(l => l.lapTimeMs).filter(t => t > 0);
    return valid.length > 0 ? Math.min(...valid) : 0;
  }, [laps]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Sensible defaults: speed & scores desc, times & distances asc
      if (['lapTime', 'distance', 'duration'].includes(field)) {
        setSortOrder('asc');
      } else {
        setSortOrder('desc');
      }
    }
  };

  const sortedLaps = useMemo(() => {
    return [...laps].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'lapNumber':
          cmp = a.lapNumber - b.lapNumber;
          break;
        case 'driver':
          cmp = (a.driverName || '').localeCompare(b.driverName || '');
          break;
        case 'lapTime':
          cmp = a.lapTimeMs - b.lapTimeMs;
          break;
        case 'initialSpeed':
          cmp = (a.initialSpeedKmh ?? 0) - (b.initialSpeedKmh ?? 0);
          break;
        case 'minSpeed':
          cmp = (a.minSpeedKmh ?? a.apexSpeedKmh ?? 0) - (b.minSpeedKmh ?? b.apexSpeedKmh ?? 0);
          break;
        case 'speedDrop':
          cmp = (a.speedDropKmh ?? 0) - (b.speedDropKmh ?? 0);
          break;
        case 'distance':
          cmp = (a.brakingDistanceM ?? 0) - (b.brakingDistanceM ?? 0);
          break;
        case 'duration':
          cmp = (a.brakingDurationMs ?? 0) - (b.brakingDurationMs ?? 0);
          break;
        case 'peakDecel':
          cmp = Math.abs(a.peakLongitudinalG ?? 0) - Math.abs(b.peakLongitudinalG ?? 0);
          break;
        case 'trailScore':
          cmp = (a.trailBrakingScore ?? 0) - (b.trailBrakingScore ?? 0);
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [laps, sortField, sortOrder]);

  const paginatedLaps = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedLaps.slice(start, start + PAGE_SIZE);
  }, [sortedLaps, page]);

  const totalPages = Math.ceil(sortedLaps.length / PAGE_SIZE);

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={12} className="opacity-40" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp size={12} className="text-accent" />
    ) : (
      <ChevronDown size={12} className="text-accent" />
    );
  };

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary">
            {t('braking.lapSpeedTable') || 'Braking Laps Breakdown'}
          </h3>
          <span className="text-xs text-text-muted">
            ({laps.length} {t('braking.lapsAnalyzed') || 'laps'})
          </span>
        </div>

        {comparisonLap && (
          <div className="flex items-center gap-2 text-xs bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-lg text-purple-300">
            <span>
              {t('braking.comparisonLap')}: {comparisonLap.driverName} (L{comparisonLap.lapNumber})
            </span>
            <button
              onClick={() => onSelectComparisonLap(null)}
              className="p-0.5 hover:bg-purple-500/20 rounded cursor-pointer transition-colors"
              title={t('braking.clearComparison') || 'Clear comparison'}
            >
              <X size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="border-b border-border-subtle bg-bg-surface/50 text-text-secondary font-medium select-none">
              <th className="py-2.5 px-3 w-10 text-center">{t('braking.colSelect')}</th>
              <th className="py-2.5 px-3 w-10 text-center">{t('braking.colCompare')}</th>
              <th
                onClick={() => handleSort('lapNumber')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary"
              >
                <div className="flex items-center gap-1">
                  <span>{t('braking.lap')}</span>
                  {renderSortIcon('lapNumber')}
                </div>
              </th>
              <th
                onClick={() => handleSort('driver')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary"
              >
                <div className="flex items-center gap-1">
                  <span>{t('braking.driver')}</span>
                  {renderSortIcon('driver')}
                </div>
              </th>
              <th
                onClick={() => handleSort('lapTime')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.lapTime')}</span>
                  {renderSortIcon('lapTime')}
                </div>
              </th>
              <th
                onClick={() => handleSort('initialSpeed')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.initialSpeed')}</span>
                  {renderSortIcon('initialSpeed')}
                </div>
              </th>
              <th
                onClick={() => handleSort('minSpeed')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.minSpeed')}</span>
                  {renderSortIcon('minSpeed')}
                </div>
              </th>
              <th
                onClick={() => handleSort('speedDrop')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.speedDrop')}</span>
                  {renderSortIcon('speedDrop')}
                </div>
              </th>
              <th
                onClick={() => handleSort('distance')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.distance')}</span>
                  {renderSortIcon('distance')}
                </div>
              </th>
              <th
                onClick={() => handleSort('peakDecel')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.peakDecel')}</span>
                  {renderSortIcon('peakDecel')}
                </div>
              </th>
              <th
                onClick={() => handleSort('trailScore')}
                className="py-2.5 px-3 cursor-pointer hover:text-text-primary text-right"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('braking.trailScore')}</span>
                  {renderSortIcon('trailScore')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle font-mono">
            {paginatedLaps.map(lap => {
              const isActive =
                activeLap?.sessionId === lap.sessionId && activeLap?.lapNumber === lap.lapNumber;
              const isComp =
                comparisonLap?.sessionId === lap.sessionId &&
                comparisonLap?.lapNumber === lap.lapNumber;
              const isBestLap = lap.lapTimeMs === bestLapTime;

              return (
                <tr
                  key={`${lap.sessionId}-${lap.lapNumber}`}
                  onClick={() => onSelectActiveLap(lap)}
                  className={`cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-accent/10 hover:bg-accent/15'
                      : isComp
                      ? 'bg-purple-500/10 hover:bg-purple-500/15'
                      : 'hover:bg-bg-hover'
                  }`}
                >
                  {/* Active Lap Radio */}
                  <td className="py-2.5 px-3 text-center" onClick={e => e.stopPropagation()}>
                    <input
                      type="radio"
                      name="active-braking-lap"
                      checked={isActive}
                      onChange={() => onSelectActiveLap(lap)}
                      className="w-3.5 h-3.5 accent-accent cursor-pointer"
                    />
                  </td>

                  {/* Comparison Lap Checkbox */}
                  <td className="py-2.5 px-3 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => onSelectComparisonLap(isComp ? null : lap)}
                      disabled={isActive}
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        isActive
                          ? 'opacity-20 cursor-not-allowed border-border-subtle'
                          : isComp
                          ? 'bg-purple-600 border-purple-500 text-white cursor-pointer'
                          : 'border-border-subtle hover:border-purple-400 cursor-pointer'
                      }`}
                      title={isComp ? t('braking.clearComparison') : t('braking.compareLap')}
                    >
                      {isComp && <Check size={11} />}
                    </button>
                  </td>

                  {/* Lap Number */}
                  <td className="py-2.5 px-3 font-semibold text-text-primary">
                    L{lap.lapNumber}
                  </td>

                  {/* Driver Name */}
                  <td className="py-2.5 px-3 font-sans text-text-secondary truncate max-w-[120px]">
                    {lap.driverName || '—'}
                  </td>

                  {/* Lap Time */}
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {isBestLap && <Trophy size={13} className="text-amber-400" />}
                      <span className={isBestLap ? 'text-amber-400 font-bold' : 'text-text-primary'}>
                        {formatTime(lap.lapTimeMs)}
                      </span>
                    </div>
                  </td>

                  {/* Initial Speed */}
                  <td className="py-2.5 px-3 text-right text-text-primary">
                    {(lap.initialSpeedKmh ?? 0).toFixed(1)} <span className="text-[10px] text-text-muted">km/h</span>
                  </td>

                  {/* Min Speed */}
                  <td className="py-2.5 px-3 text-right text-cyan-400 font-semibold">
                    {((lap.minSpeedKmh ?? lap.apexSpeedKmh) ?? 0).toFixed(1)} <span className="text-[10px] text-text-muted">km/h</span>
                  </td>

                  {/* Speed Drop */}
                  <td className="py-2.5 px-3 text-right text-rose-400">
                    -{(lap.speedDropKmh ?? 0).toFixed(1)}
                  </td>

                  {/* Distance */}
                  <td className="py-2.5 px-3 text-right text-text-primary">
                    {(lap.brakingDistanceM ?? 0).toFixed(1)}m
                  </td>

                  {/* Peak Decel */}
                  <td className="py-2.5 px-3 text-right font-semibold text-blue-400">
                    {Math.abs(lap.peakLongitudinalG ?? 0).toFixed(2)}G
                  </td>

                  {/* Trail Score */}
                  <td className="py-2.5 px-3 text-right">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${
                        (lap.trailBrakingScore ?? 0) >= 80
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : (lap.trailBrakingScore ?? 0) >= 65
                          ? 'bg-amber-500/15 text-amber-400'
                          : 'bg-red-500/15 text-red-400'
                      }`}
                    >
                      {(lap.trailBrakingScore ?? 0).toFixed(0)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-3 border-t border-border-subtle flex justify-end">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
